﻿import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { config } from '@/config/env';

interface User {
  userid: string;
  nome?: string;
  cognome?: string;
  ruolo_primario?: string;
  [key: string]: unknown;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (authData: any) => void;
  logout: () => void;
  getToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const u = sessionStorage.getItem('mU');
      return u ? JSON.parse(u) : null;
    } catch { return null; }
  });

  const isAuthenticated = !!user && !!sessionStorage.getItem('mT');

  const login = useCallback((authData: any) => {
    sessionStorage.setItem('mT', authData.t);
    sessionStorage.setItem('mR', authData.tr);
    sessionStorage.setItem('mEx', authData.tex);
    sessionStorage.setItem('mID', authData.u.userid);
    sessionStorage.setItem('mU', JSON.stringify(authData.u));
    if (authData.u.ruolo_primario) localStorage.setItem('ruolo', authData.u.ruolo_primario);
    setUser(authData.u);
  }, []);

  const logout = useCallback(() => {
    sessionStorage.clear();
    localStorage.removeItem('ruolo');
    setUser(null);
  }, []);

  const getToken = useCallback(async (): Promise<string | null> => {
    const token = sessionStorage.getItem('mT');
    const exp = sessionStorage.getItem('mEx');
    if (!token || !exp) return null;

    const needsRefresh = Date.parse(exp) - 5 * 60 * 1000 < Date.now();
    if (needsRefresh) {
      try {
        const res = await fetch(
          config.WSREST_TABATA + 'refreshtoken/' + config.SITE + '/' + config.NOME_APP,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userid: sessionStorage.getItem('mID'),
              token: sessionStorage.getItem('mR'),
            }),
          }
        );
        if (!res.ok) throw new Error('Refresh failed');
        const data = await res.json();
        sessionStorage.setItem('mT', data.t);
        sessionStorage.setItem('mEx', data.tex);
        return data.t;
      } catch {
        logout();
        return null;
      }
    }
    return token;
  }, [logout]);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, login, logout, getToken }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
